import { Alert, AlertColor, Button, Stack, Typography } from '@mui/material';
import { FC, JSX, ReactNode, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

export interface IImportAlert {
  severity: AlertColor;
  message: string;
}

/**
 * Header of an import preview grid: title, row counter, the Import button, and
 * whatever warnings the grid has to raise.
 *
 * A single alert sits on the title line, where it reads as a remark about the
 * grid. Two or more move under the title instead: inline they wrap into a block
 * that pushes the title around and stretches the Import button to the height of
 * the whole stack. The button therefore keeps its own size and stays on the
 * title line in both cases.
 */
export const TableHeader: FC<ITableHeader> = ({
  icon,
  title,
  isAllDataShown,
  dataLength,
  isRecordsWhichOverwrites,
  isDuplicates,
  shownDataLength,
  importButtonAvailable,
  alerts,
  children,
  onImport,
}) => {
  const { t } = useTranslation();

  const alertList: IImportAlert[] = alerts ?? [
    ...(isRecordsWhichOverwrites
      ? [
          {
            severity: 'info' as AlertColor,
            message: t('settings.some_existing_records_will_be_overwrited'),
          },
        ]
      : []),
    ...(isDuplicates
      ? [
          {
            severity: 'warning' as AlertColor,
            message: t('settings.there_are_some_duplicates_in_import_data'),
          },
        ]
      : []),
  ];
  const isAlertsUnderTitle = alertList.length > 1;

  return (
    <Stack sx={{ mb: '8px' }}>
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          // Without this the Import button stretches to the height of the row,
          // which is what made it a tall block next to a stack of alerts.
          alignItems: 'flex-start',
          gap: '8px',
        }}
      >
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          {icon}
          <Typography
            variant='h4'
            sx={{ fontSize: '18px', fontWeight: '500', lineHeight: '21.94px' }}
          >
            {title}
          </Typography>
          {!isAllDataShown && (
            <Typography
              variant='h4'
              sx={{ fontSize: '12px', fontWeight: '400', lineHeight: '21.94px' }}
            >
              {`(${t('common.shown')}: ${shownDataLength}/${dataLength})`}
            </Typography>
          )}
          {!isAlertsUnderTitle &&
            alertList.map((alert) => (
              <Alert key={alert.message} severity={alert.severity} sx={ALERT_SX}>
                {alert.message}
              </Alert>
            ))}
        </Stack>
        <Button
          sx={{ minWidth: '100px', flexShrink: 0 }}
          variant='contained'
          color='primary'
          size='small'
          disabled={!importButtonAvailable}
          onClick={onImport}
        >
          {t('debitor.paymentsDetailsInfo.import')}
        </Button>
      </Stack>
      {isAlertsUnderTitle && (
        <Stack sx={{ gap: '4px', mt: '8px' }}>
          {alertList.map((alert) => (
            <Alert key={alert.message} severity={alert.severity} sx={ALERT_SX}>
              {alert.message}
            </Alert>
          ))}
        </Stack>
      )}
      {children}
    </Stack>
  );
};

const ALERT_SX = {
  minHeight: 36,
  py: 0,
  px: 2,
  alignItems: 'center',
} as const;

interface ITableHeader {
  icon: JSX.Element;
  title: string;
  dataLength?: number;
  shownDataLength?: number;
  isAllDataShown: boolean;
  /** Legacy shorthand used by the customer and article grids. */
  isRecordsWhichOverwrites?: boolean;
  isDuplicates?: boolean;
  /** Explicit list; when given it replaces the two flags above. */
  alerts?: IImportAlert[];
  /** Extra controls under the header, e.g. the special prices import mode. */
  children?: ReactNode;
  onImport: MouseEventHandler<HTMLButtonElement> | undefined;
  importButtonAvailable: boolean;
}
